import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Typography,
} from '@mui/material';
import CourierLayout from '../CourierLayout';
import { API } from '../../../config';

const PerformanceAnalytics = () => {
  const [metrics, setMetrics] = useState({
    completedLoads: 0,
    activeLoads: 0,
    acceptanceRate: 0,
    averageRating: 0,
    onTimeRate: 0,
    totalDistance: 0,
  });

  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(false);

  const token =
    localStorage.getItem('courier_token') ||
    localStorage.getItem('token');

  const fetchPerformance = async () => {
    try {
      setLoading(true);

      const response = await fetch(`${API}/api/courier/performance`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        setMetrics({
          completedLoads: 0,
          activeLoads: 0,
          acceptanceRate: 0,
          averageRating: 0,
          onTimeRate: 0,
          totalDistance: 0,
        });
        setRecentActivity([]);
        return;
      }

      const data = await response.json();

      setMetrics({
        completedLoads: data?.completedLoads || data?.completed_loads || 0,
        activeLoads: data?.activeLoads || data?.active_loads || 0,
        acceptanceRate: data?.acceptanceRate || data?.acceptance_rate || 0,
        averageRating: data?.averageRating || data?.average_rating || 0,
        onTimeRate: data?.onTimeRate || data?.on_time_rate || 0,
        totalDistance: data?.totalDistance || data?.total_distance || 0,
      });

      if (Array.isArray(data?.recentActivity)) {
        setRecentActivity(data.recentActivity);
      } else if (Array.isArray(data?.recent_activity)) {
        setRecentActivity(data.recent_activity);
      } else {
        setRecentActivity([]);
      }
    } catch (error) {
      console.error('Failed to fetch performance analytics:', error);
      setMetrics({
        completedLoads: 0,
        activeLoads: 0,
        acceptanceRate: 0,
        averageRating: 0,
        onTimeRate: 0,
        totalDistance: 0,
      });
      setRecentActivity([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPerformance();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const metricCards = [
    {
      label: 'Completed Loads',
      value: metrics.completedLoads,
    },
    {
      label: 'Active Loads',
      value: metrics.activeLoads,
    },
    {
      label: 'Acceptance Rate',
      value: `${metrics.acceptanceRate}%`,
    },
    {
      label: 'On-Time Rate',
      value: `${metrics.onTimeRate}%`,
    },
    {
      label: 'Average Rating',
      value: metrics.averageRating ? `${metrics.averageRating}/5` : 'N/A',
    },
    {
      label: 'Total Distance',
      value: `${metrics.totalDistance} mi`,
    },
  ];

  return (
    <CourierLayout>
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontSize: '2rem',
            fontWeight: 700,
            lineHeight: 1.25,
            color: '#0f172a',
            mb: 1,
            textAlign: 'center',
          }}
        >
          Performance
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#64748b',
            textAlign: 'center',
            fontSize: '0.95rem',
            lineHeight: 1.5,
          }}
        >
          Review load activity, reliability, and account performance.
        </Typography>
      </Box>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {metricCards.map((metric) => (
          <Grid item xs={12} sm={6} md={4} key={metric.label}>
            <Paper
              elevation={1}
              sx={{
                p: 2.5,
                borderRadius: 3,
                border: '1px solid rgba(15, 23, 42, 0.08)',
                backgroundColor: '#ffffff',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#64748b',
                  fontWeight: 600,
                  mb: 0.75,
                }}
              >
                {metric.label}
              </Typography>

              <Typography
                variant="h6"
                sx={{
                  color: '#0f172a',
                  fontWeight: 700,
                }}
              >
                {metric.value}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Paper
        elevation={1}
        sx={{
          p: { xs: 2, md: 2.5 },
          borderRadius: 3,
          border: '1px solid rgba(15, 23, 42, 0.08)',
          backgroundColor: '#ffffff',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="subtitle1"
              sx={{
                color: '#0f172a',
                fontWeight: 700,
                mb: 0.5,
              }}
            >
              Recent Performance Activity
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '0.95rem',
              }}
            >
              Completed load activity and performance updates will appear here.
            </Typography>
          </Box>

          <Button
            variant="outlined"
            color="primary"
            onClick={fetchPerformance}
            sx={{
              minHeight: 40,
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            Refresh
          </Button>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '0.95rem',
              }}
            >
              Loading performance data...
            </Typography>
          </Paper>
        ) : recentActivity.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 4 },
              backgroundColor: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: 3,
              textAlign: 'center',
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                fontSize: '0.95rem',
              }}
            >
              Performance metrics will appear after completed loads.
            </Typography>
          </Paper>
        ) : (
          <Grid container spacing={2}>
            {recentActivity.map((item, index) => (
              <Grid item xs={12} key={item.id || index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    border: '1px solid #e2e8f0',
                    backgroundColor: '#f8fafc',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          color: '#0f172a',
                          fontWeight: 700,
                          lineHeight: 1.35,
                        }}
                      >
                        {item.title || item.description || 'Completed load'}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: '#64748b',
                          fontSize: '0.92rem',
                        }}
                      >
                        {item.created_at
                          ? new Date(item.created_at).toLocaleDateString()
                          : 'Date not available'}
                      </Typography>
                    </Box>

                    <Chip
                      label={item.status || 'Completed'}
                      size="small"
                      sx={{
                        backgroundColor: '#dcfce7',
                        color: '#166534',
                        fontWeight: 700,
                        fontSize: '0.75rem',
                      }}
                    />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>
    </CourierLayout>
  );
};

export default PerformanceAnalytics;